import { Fragment, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  contractorNested,
  type ContractorContact, type ContractRow, type ContractorBankRow,
  type VatMethod,
} from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  WITH_BUYER: 'З покупцем', WITH_SUPPLIER: 'З постачальником',
  WITH_COMMISSIONER: 'З комісіонером', WITH_COMMITTENT: 'З комітентом',
  OTHER: 'Iнше',
};
const CONTRACT_TYPES = Object.keys(CONTRACT_TYPE_LABELS);

// Метод нарахування ПЗ ПДВ за договором (alembic 0252).
const VAT_METHOD_LABELS: Record<VatMethod, string> = {
  FIRST_EVENT: 'За першою подією',
  ON_PAYMENT: 'За касовим методом (за оплатою)',
};

interface OrgOption { id: string; name: string; is_vat_payer: boolean; use_returnable_packaging: boolean }
interface CurrencyOption { id: string; code: string }
interface CivilKindOption { id: string; name: string }
interface ProjectOption { id: string; name: string }

// Read-only панель «Рахунки обліку» одного договору (розкривається по кліку).
function ContractAccountsPanelView({ contractId }: { contractId: string }) {
  const q = useQuery({
    queryKey: ['contract-accounts', contractId],
    queryFn: () => contractorNested.getContractAccounts(contractId),
  });
  if (q.isLoading) return <div style={{ padding: '0.5rem', color: 'var(--muted)' }}>Завантаження…</div>;
  if (q.isError || !q.data) return <div style={{ padding: '0.5rem', color: 'var(--muted)' }}>Не вдалося завантажити рахунки.</div>;
  const d = q.data;
  return (
    <div style={{ padding: '0.5rem 0.75rem', background: 'var(--bg-soft, #f7f7f9)', borderRadius: 4 }}>
      <div style={{ fontSize: '0.85em', color: 'var(--muted)', marginBottom: '0.4rem' }}>
        {d.is_non_resident ? 'Нерезидент' : 'Резидент'} · валюта {d.currency_code}
        {' '}· рахунки обчислюються автоматично, редагування не потрібне
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88em' }}>
        <tbody>
          {d.lines.map((ln) => (
            <tr key={ln.purpose} style={{ borderBottom: '1px solid var(--border-soft)' }}>
              <td style={{ padding: '0.25rem 0.4rem', color: 'var(--muted)' }}>{ln.purpose}</td>
              <td style={{ padding: '0.25rem 0.4rem', fontFamily: 'monospace', fontWeight: 600 }}>{ln.code}</td>
              <td style={{ padding: '0.25rem 0.4rem' }}>{ln.name ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================ Договори ============================

export function ContractsTab(props: {
  contractorId: string;
  contracts: ContractRow[];
  orgs: OrgOption[];
  currencies: CurrencyOption[];
  civilKinds: CivilKindOption[];
  projects: ProjectOption[];
  onChanged: () => void;
}) {
  const { contractorId, contracts, orgs, currencies, civilKinds, projects, onChanged } = props;
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);  // null = створення
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [orgId, setOrgId] = useState('');
  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [contractType, setContractType] = useState('OTHER');
  const [civilKindId, setCivilKindId] = useState('');
  const [contractDate, setContractDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [isZed, setIsZed] = useState(false);
  const [vatMethod, setVatMethod] = useState<VatMethod>('FIRST_EVENT');
  const [tareVatMethod, setTareVatMethod] = useState<VatMethod>('FIRST_EVENT');
  const [projectId, setProjectId] = useState('');

  const uahId = currencies.find((c) => c.code === 'UAH')?.id ?? '';
  const currCode = (cid: string) => currencies.find((c) => c.id === cid)?.code ?? '';
  // Наша юрособа договору. orgs тут — accessible-список (orgs.list); якщо
  // юрособа поза ним → прочерк. (Дзеркалить currCode.)
  const orgName = (oid: string) => orgs.find((o) => o.id === oid)?.name ?? '—';

  // Прапори ОБРАНОЇ у формі юрособи — керують умовним показом полів ПДВ.
  const selectedOrg = orgs.find((o) => o.id === orgId);
  const showVatMethod = selectedOrg?.is_vat_payer ?? false;          // п.4
  const showTareVatMethod = selectedOrg?.use_returnable_packaging ?? false; // п.3

  const resetForm = () => {
    setShowForm(false); setEditId(null);
    setOrgId(''); setName(''); setNumber(''); setCurrencyId('');
    setContractType('OTHER'); setCivilKindId(''); setContractDate('');
    setValidUntil(''); setIsZed(false); setVatMethod('FIRST_EVENT');
    setTareVatMethod('FIRST_EVENT'); setProjectId('');
  };

  const openCreate = () => { resetForm(); setShowForm(true); };
  const openEdit = (c: ContractRow) => {
    setEditId(c.id);
    setOrgId(c.organization_id);
    setName(c.name); setNumber(c.number ?? '');
    setCurrencyId(c.currency_id); setContractType(c.contract_type);
    setCivilKindId(c.civil_kind_id ?? ''); setContractDate(c.contract_date ?? '');
    setValidUntil(c.valid_until ?? ''); setIsZed(c.is_foreign_economic);
    setVatMethod(c.vat_method); setTareVatMethod(c.tare_vat_method);
    setProjectId(c.project_id ?? '');
    setShowForm(true);
  };

  const create = useMutation({
    mutationFn: () => contractorNested.createContract({
      contractor_id: contractorId, organization_id: orgId, name: name.trim(),
      currency_id: currencyId || uahId, contract_type: contractType,
      civil_kind_id: civilKindId || null, number: number.trim() || null,
      contract_date: contractDate || null, valid_until: validUntil || null,
      is_foreign_economic: isZed,
      vat_method: vatMethod, tare_vat_method: tareVatMethod,
      project_id: projectId || null,
    }),
    onSuccess: () => { resetForm(); onChanged(); },
  });
  const update = useMutation({
    mutationFn: () => contractorNested.updateContract(editId!, {
      name: name.trim(), currency_id: currencyId || uahId,
      contract_type: contractType, civil_kind_id: civilKindId || null,
      number: number.trim() || null, contract_date: contractDate || null,
      valid_until: validUntil || null, is_foreign_economic: isZed,
      vat_method: vatMethod, tare_vat_method: tareVatMethod,
      project_id: projectId || null,
    }),
    onSuccess: () => { resetForm(); onChanged(); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => contractorNested.removeContract(id),
    onSuccess: onChanged,
  });
  // Для редагування юрособа фіксована (не змінюється), тому orgId не обов'язковий.
  const canSave = !!name.trim() && (editId !== null || !!orgId)
    && !create.isPending && !update.isPending;

  return (
    <div className="tabs__panel">
      {contracts.length === 0 && <p style={{ color: 'var(--muted)' }}>Договорів немає.</p>}
      {contracts.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
              <th style={{ padding: '0.3rem' }}>Номер</th>
              <th style={{ padding: '0.3rem' }}>Назва</th>
              <th style={{ padding: '0.3rem' }}>Організація</th>
              <th style={{ padding: '0.3rem' }}>Дата</th>
              <th style={{ padding: '0.3rem' }}>Валюта</th>
              <th style={{ padding: '0.3rem' }}>Вид за ЦК</th>
              <th style={{ padding: '0.3rem' }}>Тип</th>
              <th style={{ padding: '0.3rem' }}>Метод ПДВ</th>
              <th style={{ padding: '0.3rem', textAlign: 'center' }}>ЗЕД</th>
              <th style={{ padding: '0.3rem', textAlign: 'center' }}>Рахунки</th>
              <th style={{ width: 70 }}></th>
            </tr>
          </thead>
          <tbody>
            {contracts.map((c) => (
              <Fragment key={c.id}>
                <tr style={{ borderBottom: '1px solid var(--border-soft)' }}>
                  <td style={{ padding: '0.3rem' }}>{c.number || c.code}</td>
                  <td style={{ padding: '0.3rem' }}>{c.name}</td>
                  <td style={{ padding: '0.3rem' }}>{orgName(c.organization_id)}</td>
                  <td style={{ padding: '0.3rem' }}>{c.contract_date ?? '—'}</td>
                  <td style={{ padding: '0.3rem' }}>{currCode(c.currency_id) || '—'}</td>
                  <td style={{ padding: '0.3rem' }}>
                    {c.civil_kind_id ? (civilKinds.find((k) => k.id === c.civil_kind_id)?.name ?? '—') : '—'}
                  </td>
                  <td style={{ padding: '0.3rem' }}>{CONTRACT_TYPE_LABELS[c.contract_type] ?? c.contract_type}</td>
                  <td style={{ padding: '0.3rem', fontSize: '0.85em' }}>
                    {VAT_METHOD_LABELS[c.vat_method] ?? c.vat_method}
                  </td>
                  <td style={{ padding: '0.3rem', textAlign: 'center' }}>{c.is_foreign_economic ? '✓' : ''}</td>
                  <td style={{ padding: '0.3rem', textAlign: 'center' }}>
                    <button type="button" title="Показати рахунки обліку"
                      onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}>
                      {expandedId === c.id ? '▾' : '▸'} 💳
                    </button>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button title="Редагувати" onClick={() => openEdit(c)}>✎</button>
                    <button className="danger" title="Видалити" style={{ marginLeft: '0.25rem' }}
                      onClick={() => confirm(`Видалити договір «${c.name}»?`).then((ok) => { if (ok) remove.mutate(c.id); })}>×</button>
                  </td>
                </tr>
                {expandedId === c.id && (
                  <tr>
                    <td colSpan={11} style={{ padding: '0 0.3rem 0.5rem' }}>
                      <ContractAccountsPanelView contractId={c.id} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}

      {!showForm ? (
        <button type="button" style={{ marginTop: '0.5rem' }} onClick={openCreate}>
          + Додати договір
        </button>
      ) : (
        <fieldset style={{ marginTop: '0.75rem', padding: '0.75rem', border: '1px solid #ccc' }}>
          <legend>{editId ? 'Редагування договору' : 'Новий договір'}</legend>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <label>Наша юрособа <span style={{ color: 'red' }}>*</span>
              <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
                disabled={editId !== null} style={{ display: 'block', width: '100%' }}>
                <option value="">— оберіть —</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
            <label>Валюта
              <select value={currencyId || uahId} onChange={(e) => setCurrencyId(e.target.value)} style={{ display: 'block', width: '100%' }}>
                {currencies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
              </select>
            </label>
            <label>Назва <span style={{ color: 'red' }}>*</span>
              <input value={name} onChange={(e) => setName(e.target.value)} style={{ width: '100%' }} />
            </label>
            <label>Номер
              <input value={number} onChange={(e) => setNumber(e.target.value)} style={{ width: '100%' }} />
            </label>
            <label>Тип договору
              <select value={contractType} onChange={(e) => setContractType(e.target.value)} style={{ display: 'block', width: '100%' }}>
                {CONTRACT_TYPES.map((t) => <option key={t} value={t}>{CONTRACT_TYPE_LABELS[t]}</option>)}
              </select>
            </label>
            <label>Вид за ЦК
              <select value={civilKindId} onChange={(e) => setCivilKindId(e.target.value)} style={{ display: 'block', width: '100%' }}>
                <option value="">— не вказано —</option>
                {civilKinds.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </label>
            {/* п.4: метод ПДВ — лише для платника ПДВ */}
            {showVatMethod && (
              <label>Метод ПДВ
                <select value={vatMethod} onChange={(e) => setVatMethod(e.target.value as VatMethod)} style={{ display: 'block', width: '100%' }}>
                  <option value="FIRST_EVENT">{VAT_METHOD_LABELS.FIRST_EVENT}</option>
                  <option value="ON_PAYMENT">{VAT_METHOD_LABELS.ON_PAYMENT}</option>
                </select>
              </label>
            )}
            {/* п.3: метод ПДВ по тарі — лише якщо у юрособи увімкнено облік тари */}
            {showTareVatMethod && (
              <label>Метод ПДВ по тарі
                <select value={tareVatMethod} onChange={(e) => setTareVatMethod(e.target.value as VatMethod)} style={{ display: 'block', width: '100%' }}>
                  <option value="FIRST_EVENT">{VAT_METHOD_LABELS.FIRST_EVENT}</option>
                  <option value="ON_PAYMENT">{VAT_METHOD_LABELS.ON_PAYMENT}</option>
                </select>
              </label>
            )}
            <label>Проект (за замовч.)
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)} style={{ display: 'block', width: '100%' }}>
                <option value="">— не вказано —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label>Дата
              <DatePopover value={contractDate} onChange={(v) => setContractDate(v)} style={{ width: '100%' }} />
            </label>
            <label>Строк дії
              <DatePopover value={validUntil} onChange={(v) => setValidUntil(v)} style={{ width: '100%' }} />
            </label>
          </div>
          <label style={{ display: 'block', marginTop: '0.5rem' }}>
            <input type="checkbox" checked={isZed} onChange={(e) => setIsZed(e.target.checked)} style={{ width: 'auto', marginRight: '0.4rem' }} />
            ЗЕД-договір
          </label>
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.8em', color: 'var(--muted)' }}>
            Рахунки обліку (361/362, 631/632, 6412, аванси) обчислюються автоматично —
            дивіться 💳 у списку договорів.
          </p>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
            <button className="primary" disabled={!canSave}
              onClick={() => (editId ? update.mutate() : create.mutate())}>Зберегти</button>
            <button onClick={resetForm}>Скасувати</button>
          </div>
        </fieldset>
      )}
    </div>
  );
}

// ============================ Контактні особи ============================

export function ContactsTab(props: {
  contractorId: string;
  contacts: ContractorContact[];
  onChanged: () => void;
}) {
  const { contractorId, contacts, onChanged } = props;
  const confirm = useConfirm();
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [position, setPosition] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [birthday, setBirthday] = useState('');

  const create = useMutation({
    mutationFn: () => contractorNested.createContact({
      contractor_id: contractorId, name: name.trim(),
      position: position.trim() || null, phone: phone.trim() || null,
      email: email.trim() || null, birthday: birthday || null,
      is_main: contacts.length === 0,
    }),
    onSuccess: () => { setShowAdd(false); setName(''); setPosition(''); setPhone(''); setEmail(''); setBirthday(''); onChanged(); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => contractorNested.removeContact(id),
    onSuccess: onChanged,
  });

  return (
    <div className="tabs__panel">
      {contacts.length === 0 && <p style={{ color: 'var(--muted)' }}>Контактних осіб немає.</p>}
      {contacts.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
              <th style={{ padding: '0.3rem' }}>ПIБ</th>
              <th style={{ padding: '0.3rem' }}>Посада</th>
              <th style={{ padding: '0.3rem' }}>Телефон</th>
              <th style={{ padding: '0.3rem' }}>Email</th>
              <th style={{ padding: '0.3rem' }}>День нар.</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                <td style={{ padding: '0.3rem' }}>{c.name} {c.is_main && <span style={{ color: 'var(--primary, #06c)' }}>(осн.)</span>}</td>
                <td style={{ padding: '0.3rem' }}>{c.position ?? '—'}</td>
                <td style={{ padding: '0.3rem' }}>{c.phone ?? '—'}</td>
                <td style={{ padding: '0.3rem' }}>{c.email ?? '—'}</td>
                <td style={{ padding: '0.3rem' }}>{c.birthday ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="danger" title="Видалити"
                    onClick={() => confirm(`Видалити контакт «${c.name}»?`).then((ok) => { if (ok) remove.mutate(c.id); })}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!showAdd ? (
        <button type="button" style={{ marginTop: '0.5rem' }} onClick={() => setShowAdd(true)}>+ Додати контакт</button>
      ) : (
        <fieldset style={{ marginTop: '0.75rem', padding: '0.75rem', border: '1px solid #ccc' }}>
          <legend>Новий контакт</legend>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <label>ПIБ <span style={{ color: 'red' }}>*</span>
              <input value={name} onChange={(e) => setName(e.target.value)} style={{ width: '100%' }} />
            </label>
            <label>Посада
              <input value={position} onChange={(e) => setPosition(e.target.value)} style={{ width: '100%' }} />
            </label>
            <label>Телефон
              <input value={phone} onChange={(e) => setPhone(e.target.value)} style={{ width: '100%' }} />
            </label>
            <label>Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: '100%' }} />
            </label>
            <label>День народження
              <DatePopover value={birthday} onChange={(v) => setBirthday(v)} style={{ width: '100%' }} />
            </label>
          </div>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
            <button className="primary" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>Зберегти</button>
            <button onClick={() => setShowAdd(false)}>Скасувати</button>
          </div>
        </fieldset>
      )}
    </div>
  );
}

// ============================ Банк-рахунки ============================

export function BankTab(props: {
  contractorId: string;
  accounts: ContractorBankRow[];
  currencies: CurrencyOption[];
  bankNameByIban: (iban: string) => string | null;
  onChanged: () => void;
}) {
  const { contractorId, accounts, currencies, bankNameByIban, onChanged } = props;
  const confirm = useConfirm();
  const [showAdd, setShowAdd] = useState(false);
  const [iban, setIban] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [accountName, setAccountName] = useState('');

  const uahId = currencies.find((c) => c.code === 'UAH')?.id ?? '';
  const create = useMutation({
    mutationFn: () => contractorNested.createBank({
      contractor_id: contractorId, iban: iban.replace(/\s+/g, '').toUpperCase(),
      currency_id: currencyId || uahId, account_name: accountName.trim() || null,
      is_main: accounts.length === 0,
    }),
    onSuccess: () => { setShowAdd(false); setIban(''); setAccountName(''); onChanged(); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => contractorNested.removeBank(id),
    onSuccess: onChanged,
  });
  const ibanOk = iban.replace(/\s+/g, '').length >= 15;

  return (
    <div className="tabs__panel">
      {accounts.length === 0 && <p style={{ color: 'var(--muted)' }}>Банк-рахунків немає.</p>}
      {accounts.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
              <th style={{ padding: '0.3rem' }}>IBAN</th>
              <th style={{ padding: '0.3rem' }}>Назва</th>
              <th style={{ padding: '0.3rem' }}>Осн.</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((ba) => {
              const bank = bankNameByIban(ba.iban);
              return (
                <tr key={ba.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                  <td style={{ padding: '0.3rem', fontFamily: 'monospace', fontSize: '0.85em' }}>
                    {ba.iban}
                    {bank && <div style={{ color: 'var(--muted)', fontSize: '0.9em' }}>{bank}</div>}
                  </td>
                  <td style={{ padding: '0.3rem' }}>{ba.account_name ?? '—'}</td>
                  <td style={{ padding: '0.3rem' }}>{ba.is_main ? '✓' : ''}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="danger" title="Видалити"
                      onClick={() => confirm('Видалити банк-рахунок?').then((ok) => { if (ok) remove.mutate(ba.id); })}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {!showAdd ? (
        <button type="button" style={{ marginTop: '0.5rem' }} onClick={() => setShowAdd(true)}>+ Додати банк-рахунок</button>
      ) : (
        <fieldset style={{ marginTop: '0.75rem', padding: '0.75rem', border: '1px solid #ccc' }}>
          <legend>Новий банк-рахунок</legend>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.5rem' }}>
            <label>IBAN <span style={{ color: 'red' }}>*</span>
              <input value={iban} onChange={(e) => setIban(e.target.value)} placeholder="UA..." style={{ width: '100%', fontFamily: 'monospace' }} maxLength={34} />
            </label>
            <label>Валюта
              <select value={currencyId || uahId} onChange={(e) => setCurrencyId(e.target.value)} style={{ display: 'block', width: '100%' }}>
                {currencies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
              </select>
            </label>
            <label style={{ gridColumn: '1 / -1' }}>Назва рахунку
              <input value={accountName} onChange={(e) => setAccountName(e.target.value)} style={{ width: '100%' }} />
            </label>
          </div>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
            <button className="primary" disabled={!ibanOk || create.isPending} onClick={() => create.mutate()}>Зберегти</button>
            <button onClick={() => setShowAdd(false)}>Скасувати</button>
          </div>
        </fieldset>
      )}
    </div>
  );
}
